import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { getCurrentAccount } from '../api-query/customer'
import { apiQueryClient } from '../../App';

const BFF_BASE_URL = process.env.REACT_APP_BFF_BASE_URL

export async function getPerformanceData(session: any, date?: string, interval='hour') {
    if(!session?.idToken || session?.expiryMs < Date.now()) {
        throw new Error('Session expired')
    }
    const { siteId, timezone, hasUsageData, hasTou } = getCurrentAccount();
    if(!siteId) {
        throw new Error('Loading...')
    }
    if(hasUsageData===false) {
        return { hasUsageData }
    }
    const res = await fetch(`${BFF_BASE_URL}/v1/performance/data`,
        {
            method: 'POST',
            body: JSON.stringify({ siteId, timezone, date, hasTou, interval }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.accessToken}`,
                'x-id': `${session?.idToken}`,
                'x-session-id': session?.sessionId || 'x-session-id',
                'x-correlation-id': uuidv4()
            }
        }
    );
    
    if (!res.ok) {
        throw new Error((await res.json()).message);
    }
    return (await res.json())
}

export async function getPerformanceBattery(session: any, date: string) {
    if(!session?.idToken || session?.expiryMs < Date.now()) {
        throw new Error('Session expired')
    }
    const { siteId, timezone, hasUsageData } = getCurrentAccount();
    if(!siteId) {
        throw new Error('Loading...')
    }
    if(hasUsageData===false) {
        return { hasUsageData }
    }
    const res = await fetch(`${BFF_BASE_URL}/v1/performance/battery/stateOfCharge`,
        {
            method: 'POST',
            body: JSON.stringify({ siteId, timezone, date }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.accessToken}`,
                'x-id': `${session?.idToken}`,
                'x-session-id': session?.sessionId || 'x-session-id',
                'x-correlation-id': uuidv4()
            }
        }
    );
    
    if (!res.ok) {
        throw new Error((await res.json()).message);
    }
    return (await res.json())
}

export const useGetBatteryDailyData = (date: string, session: any) => {
    let minutes = 600;
    const { timezone } = getCurrentAccount();
    const today = moment().tz(timezone).format('YYYYMMDD');
    if(date && moment.tz(moment(date),timezone).format('YYYYMMDD')===today) {
        minutes = 2
    }
    const cachedDate = moment.tz(moment(date),timezone).format('YYYYMMDD')
    const { data: dailyBatteryData } = useQuery({
        queryKey: ['BatteryDailyData', cachedDate], // key must be unique
        queryFn: ()=>getPerformanceBattery(session, date),
        staleTime: (60000 * minutes)
    })
    return { dailyBatteryData }
};

export const useGetReportDailyData = (date: string, session: any) => {
    let minutes = 600;
    const { timezone } = getCurrentAccount();
    const today = moment().tz(timezone).format('YYYYMMDD');
    if(date && moment.tz(moment(date),timezone).format('YYYYMMDD')===today) {
        minutes = 2
    }
    const cachedDate = moment.tz(moment(date),timezone).format('YYYYMMDD')
    const { isPending: reportPending, error: errorReportData, data: dailyData } = useQuery({
        queryKey: ['ReportDailyData', cachedDate], // key must be unique
        queryFn: ()=>getPerformanceData(session, date),
        staleTime: (60000 * minutes),
        initialData: () => {
            const reportsData: any = apiQueryClient
                .getQueryData(['LastFewDaysReportDailyData'])
            return reportsData?.reports
                    .find((d: any) => d.day === cachedDate)
          },
    })
    return { reportPending, errorReportData, dailyData }
};

export const useGetLastFewDaysReportData = (session: any) => {
    const { error: errorReportsData, data: reportsData } = useQuery({
        queryKey: ['LastFewDaysReportDailyData'], // key must be unique
        queryFn: ()=>getPerformanceData(session),
        staleTime: (60000 * 600)
    })
    return { errorReportsData, reportsData }
};

export const useGetReportMonthlyData = (startDate: string, session: any) => {
    const { timezone } = getCurrentAccount();
    const month = moment.tz(moment(startDate),timezone).format('YYYYMM');
    const { error: errorMontlyData, data: monthlyData } = useQuery({
        queryKey: ['ReportMonthlyData', month], // key must be unique
        queryFn: ()=>getPerformanceData(session, startDate, 'day'),
        staleTime: (60000 * 600)
    })
    return { errorMontlyData, monthlyData }
};



